import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { print } from 'graphql';

import { baseRequestHeaders } from '../lib/api-headers.ts';
import { actingAsApplication, applicationSessionExpired, loadApplicationSession } from '../lib/application-session.ts';
import { ensureAuthenticated, isExpired, saveCredentials, type Credentials } from '../lib/auth.ts';
import { clientCredentials, fetchClientCredentialsToken } from '../lib/client-credentials.ts';
import { resolveCompanyId } from '../lib/company.ts';
import { getEndpoint, getUserContext } from '../lib/config.ts';
import { error, hint, info, warn } from '../lib/output.ts';
import { addBreadcrumb, captureException } from '../lib/sentry.ts';
import { RefreshToken } from './operations/hermes.ts';

function extractOperationName(query: string): string {
  const match = query.match(/(?:mutation|query|subscription)\s+(\w+)/);
  return match?.[1] ?? 'UnknownOperation';
}

interface ResponseError {
  message: string;
  extensions?: Record<string, unknown>;
}

interface GraphQLResponse<T = Record<string, unknown>> {
  data?: T;
  errors?: ResponseError[];
}

interface RequestOptions {
  endpoint?: string;
  /** The company as the caller named it, slug or id, for messages. */
  company?: string;
  /** The same company resolved to its id. Absent, `config.company` applies. */
  companyId?: string;
  /** Send no X-Company-Id: the lookup that resolves a slug cannot itself act for one. */
  withoutCompany?: boolean;
  authenticated?: boolean;
  throwOnError?: boolean;
  /** Subgraph that owns the operation, used to explain a refusal precisely. */
  subgraph?: string;
  /**
   * Authenticate as the person even when an application credential is active.
   *
   * `sl use application` needs this: creating and revoking a credential is a
   * management operation, which Hermes refuses to an application token, so
   * switching from one application to another would otherwise be impossible
   * without stepping back through `sl use me` first.
   */
  asPerson?: boolean;
}

export class GraphQLRequestError extends Error {
  readonly operationName: string;
  readonly httpStatus?: number;
  readonly graphqlErrors?: Array<{ message: string; extensions?: Record<string, unknown> }>;

  constructor(
    message: string,
    details: {
      operationName: string;
      httpStatus?: number;
      graphqlErrors?: Array<{ message: string; extensions?: Record<string, unknown> }>;
    },
  ) {
    super(message);
    this.name = 'GraphQLRequestError';
    this.operationName = details.operationName;
    this.httpStatus = details.httpStatus;
    this.graphqlErrors = details.graphqlErrors;
  }
}

async function refreshAccessToken(creds: Credentials, endpoint: string): Promise<Credentials | null> {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: baseRequestHeaders(),
    body: JSON.stringify({
      query: print(RefreshToken),
      variables: { input: { refreshToken: creds.refreshToken } },
    }),
  });

  const json = (await res.json()) as GraphQLResponse<{
    refreshToken: {
      accessToken: string;
      refreshToken: string;
      idToken: string;
      expiresIn: number;
      error: string | null;
    };
  }>;

  const token = json.data?.refreshToken;
  if (!token || token.error) return null;

  const newCreds: Credentials = {
    accessToken: token.accessToken,
    refreshToken: token.refreshToken,
    idToken: token.idToken,
    expiresAt: Math.floor(Date.now() / 1000) + token.expiresIn,
  };

  saveCredentials(newCreds);
  return newCreds;
}

/**
 * A transport failure — nothing listening, DNS, TLS — rejects rather than
 * returning a response, so it never reaches the `!res.ok` path and escapes as an
 * undici stack trace unless it is handled here.
 */
function reportUnreachable(
  err: Error,
  { endpoint, operationName, throwOnError }: { endpoint: string; operationName: string; throwOnError: boolean },
): never {
  const code = (err as Error & { cause?: { code?: string } }).cause?.code;
  const reason = code === 'ECONNREFUSED' ? `nothing is listening on ${endpoint}` : err.message;

  captureException(err, { operationName, endpoint });
  if (throwOnError) {
    throw new GraphQLRequestError(`${operationName} could not reach the endpoint: ${reason}`, { operationName });
  }
  error(`Could not reach ${endpoint}: ${reason}`);
  hint('Is the local stack running? Check the configured endpoint with `sl config show`.');
  process.exit(3);
}

/**
 * Internal: execute a raw GraphQL query string.
 */
interface Attempt {
  endpoint: string;
  operationName: string;
  throwOnError: boolean;
  verbose: boolean;
}

/**
 * Which identity to present, in precedence order.
 *
 * An application credential wins over a stored session, and both are skipped for
 * `asPerson` — see RequestOptions.
 */
async function resolveAccessToken(
  options: RequestOptions,
  attempt: Attempt,
): Promise<{ token: string; kind: 'application' | 'user' }> {
  const machine = options.asPerson ? null : clientCredentials();
  if (machine) {
    try {
      return { token: await fetchClientCredentialsToken(machine), kind: 'application' };
    } catch (err) {
      const message = (err as Error).message;
      if (attempt.throwOnError) throw new GraphQLRequestError(message, { operationName: attempt.operationName });

      error(message);
      hint('Check SORTLIST_CLIENT_ID, SORTLIST_CLIENT_SECRET and the token endpoint.');
      hint('The credential may also have been revoked, or its application deleted.');
      process.exit(2);
    }
  }

  // Its credential is already revoked, so there is nothing to refresh with: when
  // the token expires the caller runs `sl use application` again, which needs
  // their own token.
  const session = options.asPerson ? null : loadApplicationSession();
  if (session) return { token: session.accessToken, kind: 'application' };

  if (!options.asPerson && applicationSessionExpired()) {
    error('The application session has expired.');
    hint('Run `sl use application <id>` again, or `sl use me` to act as yourself.');
    process.exit(2);
  }

  let creds = ensureAuthenticated();
  if (isExpired(creds)) {
    const refreshed = await refreshAccessToken(creds, attempt.endpoint);
    if (!refreshed) {
      if (attempt.throwOnError) {
        throw new GraphQLRequestError('Session expired and could not be refreshed.', {
          operationName: attempt.operationName,
        });
      }
      error('Your session has expired and could not be refreshed.');
      hint('Run `sl login` to re-authenticate.');
      process.exit(2);
    }
    creds = refreshed;
  }

  return { token: creds.accessToken, kind: 'user' };
}

function reportHttpFailure(res: Response, body: string, attempt: Attempt): never {
  const { operationName, endpoint, throwOnError, verbose } = attempt;
  captureException(new Error(`${operationName} failed: HTTP ${res.status}`), {
    operationName,
    httpStatus: res.status,
    endpoint,
  });
  if (throwOnError) {
    throw new GraphQLRequestError(`${operationName} failed: HTTP ${res.status} ${res.statusText}`, {
      operationName,
      httpStatus: res.status,
    });
  }

  error(`${operationName} failed: HTTP ${res.status} ${res.statusText}`);
  hint('Check your network connection and endpoint: sl config show');
  if (verbose) info(`[debug] Response body: ${body}`);
  process.exit(3);
}

/** A selection the subgraph could not resolve; `--fields` is the way out. */
function hasFieldError(errors: ResponseError[]): boolean {
  return errors.some((e) => e.message.includes('Failed to implement') || e.message.includes('undefined method'));
}

/**
 * The router replaces a subgraph's message with "Unexpected error" but keeps its
 * `extensions.code`, so the code is the only part worth reading — which is also
 * what the platform's error contract says: the message is a log line, the code is
 * the contract.
 */
function codeOf(errors: ResponseError[]): string | null {
  for (const { extensions } of errors) {
    const code = extensions?.code;
    if (typeof code === 'string') return code;
  }

  return null;
}

/** `VALIDATION_FAILED` reads better as a sentence than as a constant. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function subrequestStatus(errors: ResponseError[]): number | null {
  for (const { extensions } of errors) {
    const http = extensions?.http;
    if (typeof http === 'object' && http !== null && typeof (http as { status?: unknown }).status === 'number') {
      return (http as { status: number }).status;
    }
  }

  return null;
}

function humanise(code: string): string {
  const words = code.toLowerCase().replaceAll('_', ' ');

  return words.charAt(0).toUpperCase() + words.slice(1) + '.';
}

/**
 * `{ name: ["taken"] }` becomes `name: taken`.
 *
 * The shape is the platform's error contract: an attribute mapped to symbolic
 * codes, never prose, so this only has to lay it out.
 */
function detailLines(errors: ResponseError[]): string[] {
  const lines: string[] = [];
  for (const { extensions } of errors) {
    const details = extensions?.details;
    if (typeof details !== 'object' || details === null) continue;

    for (const [attribute, codes] of Object.entries(details as Record<string, unknown>)) {
      lines.push(`${attribute}: ${Array.isArray(codes) ? codes.join(', ') : String(codes)}`);
    }
  }

  return lines;
}

const ROUTER_PLACEHOLDER = 'Unexpected error';

/**
 * One sentence for a caller that catches rather than exits.
 *
 * Built from the code and its details for the same reason the printed path is:
 * joining the messages produced "Unexpected error; Unexpected error", which is
 * the router's placeholder twice and says nothing.
 */
function describe(errors: ResponseError[]): string {
  const code = codeOf(errors);
  const details = detailLines(errors);
  const messages = messageLines(errors);

  if (code !== null && code !== 'DOWNSTREAM_SERVICE_ERROR') {
    const explanation = details.length > 0 ? details : messages;
    return explanation.length > 0 ? `${humanise(code)} ${explanation.join('; ')}` : humanise(code);
  }

  // Falling back to `code` here would print DOWNSTREAM_SERVICE_ERROR, which names
  // the router's own plumbing and explains nothing to whoever ran the command.
  return messages.length > 0 ? messages.join('; ') : 'the server gave no reason';
}

function messageLines(errors: ResponseError[]): string[] {
  return errors.map((e) => e.message).filter((message) => message !== ROUTER_PLACEHOLDER);
}

function serviceOf(errors: ResponseError[]): string | null {
  for (const { extensions } of errors) {
    const service = extensions?.service;
    if (typeof service === 'string') return service;
  }

  return null;
}

/** Did anything actually resolve? An all-null payload beside an error is a failure. */
function hasResolvedField(data: unknown): boolean {
  if (data === null || data === undefined) return false;

  return Object.values(data as Record<string, unknown>).some((value) => value !== null);
}

function reportGraphqlErrors(errors: ResponseError[], options: RequestOptions, attempt: Attempt): never {
  const { operationName, throwOnError, verbose } = attempt;
  captureException(new Error(`${operationName} GraphQL errors`), {
    operationName,
    errors: errors.map((e) => e.message),
  });

  if (throwOnError) {
    throw new GraphQLRequestError(`${operationName} failed: ${describe(errors)}`, {
      operationName,
      graphqlErrors: errors,
    });
  }

  // Relaying "Unexpected error" teaches nobody anything; the code does.
  if (codeOf(errors) === 'NOT_AUTHENTICATED') {
    const service = serviceOf(errors);
    const where = service === null ? '' : ` for ${service}`;
    error(`Not authenticated${where}.`);
    if (actingAsApplication()) {
      hint('You are acting as an application. Hermes refuses the tokens it mints on its own');
      hint('management API by design, so a leaked secret cannot mint more — `sl use me` switches back.');
    } else {
      hint('Run `sl login` to authenticate, or `sl whoami` to see what the CLI is acting as.');
    }
    if (verbose) for (const gqlError of errors) info(`[debug] ${gqlError.message}`);
    process.exit(4);
  }

  // A 401 from a subgraph means the token no longer verifies there: expired, or
  // signed with a key the issuer has since rotated. The router relays neither.
  if (subrequestStatus(errors) === 401) {
    const service = serviceOf(errors);
    const where = service ?? 'The subgraph';
    error(`${where} did not accept your token.`);
    if (actingAsApplication()) {
      hint('The application token has expired or was signed with a key Hermes no longer publishes');
      hint('(a local Hermes mints a fresh key on every restart). Run `sl use application <id>` again.');
    } else {
      hint('Run `sl login` again; `sl whoami` shows what the CLI is acting as.');
    }
    if (verbose) for (const gqlError of errors) info(`[debug] ${JSON.stringify(gqlError)}`);
    process.exit(4);
  }

  // A 403 from a subgraph is the X-Company-Id check: the router hides the
  // subgraph's message, so the CLI has to say what it sent.
  if (subrequestStatus(errors) === 403) {
    const service = serviceOf(errors);
    const where = service ?? 'The subgraph';
    const company = options.company ?? getUserContext().company ?? options.companyId;
    if (company && UUID.test(company)) {
      error(`${where} refused ${company}: that is a raw uuid, not a company id.`);
      hint('Pass its slug, or the id `sl companies` lists.');
    } else if (company) {
      error(`${where} refused to act for company ${company}.`);
      hint('Your token does not list that company, or Hermes no longer knows you as one of its members.');
      hint('`sl whoami` shows what the CLI is acting as;');
      hint('pick one of your companies with --company or `sl config set company <slug|id>`.');
    } else {
      error(`${where} refused this request: it names no company to act for.`);
      hint('Pick one of your companies with --company or `sl config set company <slug|id>`;');
      hint('`sl companies` lists them. Join one first if the list is empty.');
    }
    if (verbose) for (const gqlError of errors) info(`[debug] ${JSON.stringify(gqlError)}`);
    process.exit(4);
  }

  // Prefer the code and its details over the message: the router replaces the
  // message with a placeholder, and the platform's error contract puts the
  // machine-readable part in extensions precisely so a client need not parse prose.
  const code = codeOf(errors);
  if (code !== null && code !== 'DOWNSTREAM_SERVICE_ERROR') {
    error(humanise(code));

    // Details are the contract and say it better. Absent them the message is all
    // there is — except when it is the router's placeholder, which says nothing.
    const details = detailLines(errors);
    const lines = details.length > 0 ? details : messageLines(errors);
    for (const line of lines) info(`  ${line}`);
    if (verbose) for (const gqlError of errors) info(`[debug] ${JSON.stringify(gqlError)}`);
    process.exit(1);
  }

  error(`${operationName} failed:`);
  for (const gqlError of errors) {
    info(`  ${gqlError.message}`);
    if (verbose && gqlError.extensions) info(`[debug] Extensions: ${JSON.stringify(gqlError.extensions)}`);
  }

  if (hasFieldError(errors)) {
    hint('Use --fields to select specific fields, or --exclude to skip broken ones.');
    hint(`Example: sl ${operationName.toLowerCase()} --fields id,title,status`);
  }

  process.exit(1);
}

function logRequest(query: string, variables: Record<string, unknown> | undefined, attempt: Attempt): void {
  info(`[debug] ${attempt.operationName} → ${attempt.endpoint}`);
  info(`[debug] Query:\n${query}`);
  if (variables) info(`[debug] Variables: ${JSON.stringify(variables, null, 2)}`);
}

async function executeQuery<T = Record<string, unknown>>(
  query: string,
  variables: Record<string, unknown> | undefined,
  options: RequestOptions = {},
): Promise<T> {
  const authenticated = options.authenticated ?? true;
  const attempt: Attempt = {
    endpoint: getEndpoint(options.endpoint),
    operationName: extractOperationName(query),
    throwOnError: options.throwOnError ?? false,
    verbose: process.env.SL_DEBUG === '1',
  };

  addBreadcrumb({
    category: 'graphql',
    message: attempt.operationName,
    data: { endpoint: attempt.endpoint, authenticated },
    level: 'info',
  });

  if (attempt.verbose) logRequest(query, variables, attempt);

  const headers: Record<string, string> = baseRequestHeaders();

  if (authenticated) {
    const { token, kind } = await resolveAccessToken(options, attempt);
    headers['Authorization'] = `Bearer ${token}`;
    const companyId = options.withoutCompany
      ? undefined
      : (options.companyId ?? (await resolveCompanyId({ ctx: getUserContext() })));
    if (companyId) headers['X-Company-Id'] = companyId;
    // Never print the full token — debug output ends up in pasted logs. `sl token`
    // is the deliberate way to get the raw value.
    if (attempt.verbose) info(`[debug] ${kind} token: ...${token.slice(-8)}`);
  }

  let res: Response;
  try {
    res = await fetch(attempt.endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, variables }),
    });
  } catch (err) {
    reportUnreachable(err as Error, {
      endpoint: attempt.endpoint,
      operationName: attempt.operationName,
      throwOnError: attempt.throwOnError,
    });
  }

  if (!res.ok) reportHttpFailure(res, await res.text(), attempt);

  const json = (await res.json()) as GraphQLResponse<T>;

  if (json.errors?.length) {
    // A refused mutation arrives as data carrying a null field *and* an error, so
    // the refusal has to be recognised before the partial-success branch — which
    // would otherwise relay the router's placeholder and let the caller explain
    // the null a second time, in different words.
    const refused = !hasResolvedField(json.data);
    if (refused) reportGraphqlErrors(json.errors, options, attempt);

    // Partial success: the data that did resolve is worth more than the failure.
    for (const gqlError of json.errors) warn(gqlError.message);
    if (hasFieldError(json.errors)) {
      hint('Use --fields to select specific fields, or --exclude to skip broken ones.');
    }
    return json.data as T;
  }

  if (!json.data) {
    if (attempt.throwOnError) {
      throw new GraphQLRequestError(`${attempt.operationName}: no data returned from API.`, {
        operationName: attempt.operationName,
      });
    }
    error(`${attempt.operationName}: no data returned from API.`);
    hint('This usually means the server returned an empty response.');
    hint('Verify your endpoint: sl config show');
    process.exit(1);
  }

  return json.data;
}

/**
 * Execute a typed GraphQL operation (for hand-written commands).
 */
export async function graphql<TData, TVars>(
  document: TypedDocumentNode<TData, TVars>,
  variables?: TVars,
  options: RequestOptions = {},
): Promise<TData> {
  return executeQuery<TData>(print(document), variables as Record<string, unknown> | undefined, options);
}

/**
 * Execute a raw GraphQL query string (for generated/dynamic commands).
 */
export async function graphqlRaw(
  query: string,
  variables?: Record<string, unknown>,
  options: RequestOptions = {},
): Promise<Record<string, unknown>> {
  return executeQuery(query, variables, options);
}

/**
 * Execute an unauthenticated typed GraphQL operation.
 */
export async function graphqlUnauthenticated<TData, TVars>(
  document: TypedDocumentNode<TData, TVars>,
  variables?: TVars,
  endpoint?: string,
): Promise<TData> {
  return graphql(document, variables, { endpoint, authenticated: false });
}
